import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ToastProvider } from './contexts/ToastContext'
import { PersonProvider, usePerson } from './contexts/PersonContext'
import { AppShell } from './components/shell/AppShell'
import { RouteGuard } from './components/routing/RouteGuard'
import { HomePage } from './pages/HomePage'
// Wave-2 real pages, wired by the LEAD from each builder's registration manifest.
import { LoginPage } from './pages/LoginPage'
import { SignupPage } from './pages/SignupPage'
import { OnboardPersonPage } from './pages/OnboardPersonPage'
import { CirclePage } from './pages/CirclePage'
import { InvitationAcceptPage } from './pages/InvitationAcceptPage'
import { GroupPage } from './pages/GroupPage'
import { GroupInvitationsPage } from './pages/GroupInvitationsPage'
import { GroupObservationPage } from './pages/GroupObservationPage'
import { FamilySettingsPage } from './pages/FamilySettingsPage'
import { PasswordRecoveryPage } from './pages/PasswordRecoveryPage'
import { EmailVerificationPage } from './pages/EmailVerificationPage'
import { BrowsePage } from './pages/BrowsePage'
import { RoomPage } from './pages/RoomPage'
import { ApprovalsPage } from './pages/ApprovalsPage'
import { FindFriendsPage } from './pages/FindFriendsPage'
import { MyFriendsPage } from './pages/MyFriendsPage'
import { ReportPage } from './pages/ReportPage'
import { SafetyCentrePage } from './pages/SafetyCentrePage'
import { KindnessCodePage } from './pages/KindnessCodePage'
import { AboutPage } from './pages/AboutPage'
import { VerificationPage } from './pages/VerificationPage'
import { canEnterCommunity } from './lib/verificationStatus'
import { Button } from './components/ui/Button'
import { rememberReturnPath, withReturnPath } from './lib/returnPath'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-page">
      <p className="font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
    </div>
  )
}

/** Auth gate → PersonProvider → onboarding gate → shell. */
function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to={withReturnPath('/login', location.pathname + location.search)} replace state={{ from: location.pathname + location.search }} />
  return (
    <PersonProvider>
      <ShellGate />
    </PersonProvider>
  )
}

export function ShellGate() {
  const { loading, needsOnboarding, verification, error, refresh, refreshing } = usePerson()
  const location = useLocation()
  if (loading) return <LoadingScreen />
  if (needsOnboarding) {
    rememberReturnPath(location.pathname + location.search)
    return <Navigate to={withReturnPath('/signup', location.pathname + location.search)} replace />
  }
  if (error) return <AccountRecovery refreshing={refreshing} onRetry={refresh} />
  if (!canEnterCommunity(verification)) {
    rememberReturnPath(location.pathname + location.search)
    return <Navigate to={withReturnPath('/verification', location.pathname + location.search)} replace />
  }
  return <AppShell />
}

function AccountRecovery({ refreshing, onRetry }: { refreshing?: boolean; onRetry: () => unknown }) {
  return <div className="flex min-h-screen items-center justify-center bg-paper-page px-5 py-10">
    <div role="alert" className="w-full max-w-md rounded-card border border-line bg-paper-surface p-6 shadow-card">
      <h1 className="font-serif text-2xl text-ink">Your account could not be loaded.</h1>
      <p className="mt-3 text-sm leading-relaxed text-ink-muted">Check your connection and try again. Your community will open after your account is available.</p>
      <Button className="mt-5" disabled={refreshing} onClick={() => void onRetry()}>{refreshing ? 'Retrying…' : 'Retry account'}</Button>
    </div>
  </div>
}

export function VerificationAccountGate() {
  const { loading, error, refreshing, refresh } = usePerson()
  if (loading) return <LoadingScreen />
  if (error) return <AccountRecovery refreshing={refreshing} onRetry={refresh} />
  return <VerificationPage />
}

export function VerificationAuthGate() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <PersonProvider><VerificationAccountGate /></PersonProvider>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              {/* Public — no shell (Wave-2: COTC_SignupConsent replaces these). */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
              <Route path="/reset-password" element={<PasswordRecoveryPage reset />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/confirm-email" element={<EmailVerificationPage code />} />
              <Route path="/verification" element={<VerificationAuthGate />} />
              <Route path="/invitations/:token" element={<InvitationAcceptPage />} />

              {/* Authenticated shell. */}
              <Route element={<RequireAuth />}>
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/approvals"
                  element={
                    <RouteGuard denyKid denySolo>
                      <ApprovalsPage />
                    </RouteGuard>
                  }
                />
                <Route path="/circles/:id" element={<CirclePage />} />
                <Route path="/groups/:id" element={<GroupPage />} />
                <Route path="/groups/:id/observe" element={<RouteGuard denyKid><GroupObservationPage /></RouteGuard>} />
                <Route path="/group-invitations" element={<GroupInvitationsPage />} />
                <Route path="/family" element={<RouteGuard denyKid><FamilySettingsPage /></RouteGuard>} />
                <Route path="/rooms/:id" element={<RoomPage />} />
                <Route path="/browse" element={<BrowsePage />} />
                <Route path="/find" element={<FindFriendsPage />} />
                <Route path="/friends" element={<MyFriendsPage />} />
                <Route path="/report" element={<ReportPage />} />
                <Route
                  path="/safety"
                  element={
                    <RouteGuard denyKid>
                      <SafetyCentrePage />
                    </RouteGuard>
                  }
                />
                <Route path="/rules" element={<KindnessCodePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route
                  path="/onboard"
                  element={
                    <RouteGuard denyKid denySolo>
                      <OnboardPersonPage />
                    </RouteGuard>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
